"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const validationError_1 = require("./validationError");
const toError_1 = require("./toError");
function isError(error) {
    return (error != null &&
        typeof error === "object" &&
        typeof error.name === "string" &&
        typeof error.message === "string");
}
function isValidationError(error) {
    return isError(error) && "value" in error;
}
function throwValidationError(expectedType, value) {
    // We avoid logging the actual value into 'message' in case it has PII
    // If a caller really needs the value, they can extract it from the 'value' prop
    // Note - a typeof value of "object" can mean many things (null, array, Date, actual JSON object).
    // So for special cases of "object" which may be interesting, we'll override the logged string type
    // for easier diagnosability of failures.
    let typeDescription;
    if (value === null) {
        typeDescription = "null";
    }
    else if (typeof value === "object") {
        // e.g., [object Array], [object Date], [object Object]
        typeDescription = Object.prototype.toString.call(value);
    }
    else {
        typeDescription = `type '${typeof value}'`;
    }
    throw new validationError_1.ValidationError(`Expected type '${expectedType}', received ${typeDescription}.`, value);
}
function rethrowWithKey(error, containerValue, key) {
    if (isError(error)) {
        const message = `Invalid value at key '${key}'. ${error.message}`;
        try {
            // Update message on existing error to include key. This should preserve the existing callstack.
            error.message = message;
        }
        catch (typeError) {
            // Unable to modify existing error object so we need to create a new one.
            // NOTE: This will start a new error callstack.
            throw new validationError_1.ValidationError(message, isValidationError(error) ? error.value : containerValue);
        }
        throw error;
    }
    // Something weird was thrown by a user validation function.
    throw new validationError_1.ValidationError(`Invalid value at key '${key}'.`, containerValue);
}
function rethrowWithIndex(error, containerValue, index) {
    if (isError(error)) {
        const message = `Invalid value at index '${index}'. ${error.message}`;
        try {
            // Update message on existing error to include key. This should preserve the existing callstack.
            error.message = message;
        }
        catch (typeError) {
            // Unable to modify existing error object so we need to create a new one.
            // NOTE: This will start a new error callstack.
            throw new validationError_1.ValidationError(message, isValidationError(error) ? error.value : containerValue);
        }
        throw error;
    }
    // Something weird was thrown by a user validation function.
    throw new validationError_1.ValidationError(`Invalid value at index '${index}'.`, containerValue);
}
/* ============================================================================
                                asBool
============================================================================ */
/**
 * Validates a value is a boolean
 * @param value The value to check
 * @returns The value
 * @throws ValidationError when value is not a boolean
 */
function asBool(value) {
    if (typeof value === "boolean") {
        return value;
    }
    throwValidationError("boolean", value);
}
exports.asBool = asBool;
/**
 * Validates a value is a boolean, null, or undefined
 * @param value The value to check
 * @returns The value or undefined. Null is converted to undefined.
 * @throws ValidationError when value is the wrong type
 */
function asOptionalBool(value) {
    if (typeof value === "boolean") {
        return value;
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("boolean?", value);
}
exports.asOptionalBool = asOptionalBool;
/* ============================================================================
                                asString
============================================================================ */
/**
 * Validates a value is a string
 * @param value The value to check
 * @returns The value
 * @throws ValidationError when value is not a string
 */
function asString(value) {
    if (typeof value === "string") {
        return value;
    }
    throwValidationError("string", value);
}
exports.asString = asString;
/**
 * Validates a value is a string, null, or undefined
 * @param value The value to check
 * @returns The value or undefined. Null is converted to undefined.
 * @throws ValidationError when value is the wrong type
 */
function asOptionalString(value) {
    if (typeof value === "string") {
        return value;
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("string?", value);
}
exports.asOptionalString = asOptionalString;
/* ============================================================================
                                asNumber
============================================================================ */
/**
 * Validates a value is a number
 * @param value The value to check
 * @returns The value
 * @throws ValidationError when value is not a number
 */
function asNumber(value) {
    if (typeof value === "number") {
        return value;
    }
    throwValidationError("number", value);
}
exports.asNumber = asNumber;
/**
 * Validates a value is a number, null, or undefined
 * @param value The value to check
 * @returns The value or undefined. Null is converted to undefined.
 * @throws ValidationError when value is the wrong type
 */
function asOptionalNumber(value) {
    if (typeof value === "number") {
        return value;
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("number?", value);
}
exports.asOptionalNumber = asOptionalNumber;
/* ============================================================================
                                asDate
============================================================================ */
/**
 * Validates a value is a valid Date or a formatted date string
 * @param value The value to check
 * @returns The value in the form of a Date object
 * @throws ValidationError when value is not a date
 */
function asDate(value) {
    if (typeof value === "string") {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    else if (value instanceof Date) {
        if (!isNaN(value.getTime())) {
            return value;
        }
    }
    throwValidationError("Date", value);
}
exports.asDate = asDate;
/**
 * Validates a value is a valid Date or a formatted date string, null, or undefined
 * @param value The value to check
 * @returns The value in the form of a Date object, or undefined. Null is converted to undefined.
 * @throws ValidationError when value is the wrong type
 */
function asOptionalDate(value) {
    if (typeof value === "string") {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    else if (value instanceof Date) {
        if (!isNaN(value.getTime())) {
            return value;
        }
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("Date?", value);
}
exports.asOptionalDate = asOptionalDate;
/* ============================================================================
                                asArray
============================================================================ */
function _asArray(value, processor) {
    if (processor) {
        return value.map((item, index) => {
            try {
                return processor(item);
            }
            catch (error) {
                rethrowWithIndex(error, value, index);
            }
        });
    }
    else {
        return value;
    }
}
/**
 * Validates a value is an array
 * @param value The value to check
 * @param processor An optional function that validates an array's element type and may transform it to another type.
 * @returns The value
 * @throws ValidationError when value is not an array. Any error thrown by `processor`.
 */
function asArray(value, processor) {
    if (Array.isArray(value)) {
        return _asArray(value, processor);
    }
    throwValidationError("Array", value);
}
exports.asArray = asArray;
function asOptionalArray(value, processor) {
    if (Array.isArray(value)) {
        return _asArray(value, processor);
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("Array?", value);
}
exports.asOptionalArray = asOptionalArray;
/* ============================================================================
                                asCompactArray
============================================================================ */
function _asCompactArray(value, processor, onError) {
    const result = [];
    value.forEach(element => {
        var _a;
        try {
            const mapped = processor(element);
            // Check if null or undefined
            if (mapped != null) {
                result.push(mapped);
            }
        }
        catch (error) {
            /** Throw out element thrown by caller. */
            (_a = onError) === null || _a === void 0 ? void 0 : _a(element, toError_1.toError(error));
        }
    });
    return result;
}
/**
 * Validates a value is an array. Returns an array containing the non-null and non-thrown results of calling
 * the given transformation with each element of this array.
 * @param value The value to check
 * @param processor A function that validates an array's element type and
 * may transform it to another type. May return null, undefined, or throw an error if the value is to be not included
 * in the returned array.
 * @param onError A function that is called with the element that fails a processor call and the error it produced.
 * @returns A new array containing the transformed contents of value
 * @throws ValidationError when value is not an array
 */
function asCompactArray(value, processor, onError) {
    if (Array.isArray(value)) {
        return _asCompactArray(value, processor, onError);
    }
    throwValidationError("Array", value);
}
exports.asCompactArray = asCompactArray;
/**
 * Validates a value is an array, undefined, or null. Returns an array containing the non-null
 * and non-thrown results of calling the given transformation with each element of this array.
 * @param value The value to check
 * @param processor A function that validates an array's element type and
 * may transform it to another type. May return null, undefined, or throw an error if the value is to be not included
 * in the returned array.
 * @param onError A function that is called with the element that fails a processor call and the error it produced.
 * @returns A new array containing the transformed contents of value
 * @throws ValidationError when value is not an array
 */
function asOptionalCompactArray(value, processor, onError) {
    if (Array.isArray(value)) {
        return _asCompactArray(value, processor, onError);
    }
    else if (value == null) {
        return undefined;
    }
    throwValidationError("Array?", value);
}
exports.asOptionalCompactArray = asOptionalCompactArray;
/* ============================================================================
                                asObject
============================================================================ */
function _asObject(value, memberValidator) {
    if (memberValidator) {
        const result = {};
        for (const key of Object.keys(value)) {
            try {
                result[key] = memberValidator(value[key], key);
            }
            catch (error) {
                rethrowWithKey(error, value, key);
            }
        }
        return result;
    }
    else {
        return value;
    }
}
function asObject(value, memberValidator) {
    if (typeof value === "object" && value != null && !Array.isArray(value)) {
        return _asObject(value, memberValidator);
    }
    else {
        throwValidationError("object", value);
    }
}
exports.asObject = asObject;
function asOptionalObject(value, memberValidator) {
    if (typeof value === "object" && value != null && !Array.isArray(value)) {
        return _asObject(value, memberValidator);
    }
    else if (value == null) {
        return undefined;
    }
    else {
        throwValidationError("object?", value);
    }
}
exports.asOptionalObject = asOptionalObject;
/* ============================================================================
                            asObjectWithSchema
============================================================================ */
function _asObjectWithSchema(value, memberValidator, strict) {
    const result = {};
    // In strict mode, extra keys must not exist in the source object
    if (strict) {
        for (const key of Object.keys(value)) {
            if (memberValidator[key] === undefined) {
                throw new validationError_1.ValidationError(`Unexpected key '${key}' in value.`, value);
            }
        }
    }
    // Every validator must run and pass
    for (const [key, validator] of Object.entries(memberValidator)) {
        try {
            const extractedValue = validator(value[key]);
            if (extractedValue !== undefined) {
                result[key] = extractedValue;
            }
        }
        catch (error) {
            rethrowWithKey(error, value, key);
        }
    }
    return result;
}
/**
 * Validates a value is an object with a specific schema.
 * @param value The value to check
 * @param memberValidator An object with validators for each key that should be extracted and validated.
 * @param strict When true, the value must match 1:1 with the validator's fields. When false, the value can have additional fields that are ignored.
 * @returns A new object with only key/value pairs that were parsed
 * @throws ValidationError when value does not match the schema
 */
function asObjectWithSchema(value, memberValidator, strict = false) {
    if (typeof value === "object" && value != null && !Array.isArray(value)) {
        return _asObjectWithSchema(value, memberValidator, strict);
    }
    else {
        throwValidationError("object", value);
    }
}
exports.asObjectWithSchema = asObjectWithSchema;
/**
 * Validates a value is an object with a specific schema, undefined, or null
 * @param value The value to check
 * @param memberValidator An object with validators for each key that should be extracted and validated.
 * @param strict When true, the value must match 1:1 with the validator's fields. When false, the value can have additional fields that are ignored.
 * @returns A new object with only key/value pairs that were parsed, or undefined. Null values are converted to undefined.
 * @throws ValidationError when value does not match the schema
 */
function asOptionalObjectWithSchema(value, memberValidator, strict = false) {
    if (typeof value === "object" && value != null && !Array.isArray(value)) {
        return _asObjectWithSchema(value, memberValidator, strict);
    }
    else if (value == null) {
        return undefined;
    }
    else {
        throwValidationError("object?", value);
    }
}
exports.asOptionalObjectWithSchema = asOptionalObjectWithSchema;
//# sourceMappingURL=asPrimitive.js.map